<template>
  <div class="news-detail-page">
    <!-- loading -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>در حال دریافت خبر...</p>
    </div>

    <!-- خطا -->
    <div v-else-if="error" class="error-state">
      <i class="fas fa-exclamation-circle"></i>
      <p>{{ error }}</p>
      <router-link to="/news" class="back-btn">بازگشت به اخبار</router-link>
    </div>

    <!-- محتوا -->
    <div v-else-if="news" class="detail-container">

      <!-- breadcrumb -->
      <div class="breadcrumb">
        <router-link to="/">خانه</router-link>
        <i class="fas fa-chevron-left"></i>
        <router-link to="/news">اخبار</router-link>
        <i class="fas fa-chevron-left"></i>
        <span>{{ news.title }}</span>
      </div>

      <article class="article">

        <!-- هدر مقاله -->
        <div class="article-header">
          <span :class="['source-badge', `badge-${news.sourceName}`]">
               {{ sourceLabel(news.source) }}
          </span>
          <h1 class="article-title">{{ news.title }}</h1>
          <div class="article-meta">
            <span><i class="far fa-user"></i> {{ news.author }}</span>
            <span><i class="far fa-clock"></i> {{ formatDate(news.publishDate) }}</span>
            <span><i class="far fa-eye"></i> {{ news.views }} بازدید</span>
          </div>
        </div>

        <!-- تصویر -->
        <div v-if="news.imageUrl" class="article-image">
          <img :src="proxyImage(news.imageUrl)" :alt="news.title" @error="onImgError"/>
        </div>

        <!-- خلاصه -->
        <div class="article-summary">
          {{ news.summary }}
        </div>

        <!-- تگ‌های مرتبط (لیگ/تیم/بازیکن/مربی) -->
        <div v-if="tags.length" class="article-tags">
          <router-link
              v-for="tag in tags"
              :key="tag.to"
              :to="tag.to"
              class="tag-chip"
              :class="`tag-${tag.kind.toLowerCase()}`"
          >
            <img v-if="tag.logo" :src="tag.logo" class="tag-logo" :alt="tag.name"/>
            <i v-else :class="['fas', tag.icon]"></i>
            <span>{{ tag.name }}</span>
          </router-link>
        </div>

        <!-- محتوا -->
        <!-- محتوای کامل خبر -->
        <div class="article-content">

          <template v-if="news.content && news.content.length > 100">

            <p
                v-for="(para, i) in richParagraphs"
                :key="i"
                class="para"
            >
              <template v-for="(tok, j) in para.tokens">
                <router-link
                    v-if="tok.to"
                    :key="`${i}-${j}`"
                    :to="tok.to"
                    class="entity-link"
                >{{ tok.text }}</router-link>
                <span v-else :key="`${i}-${j}`">{{ tok.text }}</span>
              </template>
            </p>

          </template>

          <div v-else class="no-content">
            <i class="fas fa-info-circle"></i>
            متن کامل این خبر در سایت مرجع موجود است.
          </div>

        </div>

        <!-- فوتر -->
        <div class="article-footer">

          <a
              v-if="news.url"
              :href="news.url"
              target="_blank"
              rel="noopener noreferrer"
              class="source-link"
          >
            <i class="fas fa-external-link-alt"></i>
            مشاهده در {{ sourceLabel(news.source) }}
          </a>

          <router-link to="/news" class="back-link">
            <i class="fas fa-arrow-right"></i>
            بازگشت به اخبار
          </router-link>

        </div>

      </article>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api'
import { setSeo } from '@/utils/seo'
import { teamNames, leagueNamesLatin, playerNames, coachNameFa } from '@/utils/translations'

// الگوی جستجوی یک نام در متن — بی‌حساسیت به فاصله/نیم‌فاصله و ی/ك
function nameToRegex(name) {
  const raw = String(name || '').trim();
  if (!raw) return null;
  const parts = raw.split(/\s+/).filter(Boolean);
  if (!parts.length) return null;
  const esc = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const body = parts
      .map(w => esc(w).replace(/[یي]/g, '[یي]').replace(/[كک]/g, '[كک]'))
      .join('[\\s\\u200c]*');
  const isLatin = /^[A-Za-z0-9 .'’-]+$/.test(raw);
  try {
    return new RegExp(isLatin ? `\\b(?:${body})\\b` : body, 'i');
  } catch (e) { return null; }
}

export default {
  name: 'NewsDetail',

  data() {
    return {
      news: null,
      loading: true,
      error: null,
    }
  },

  computed: {
    // تگ‌های مرتبط خبر — از NewsEntity بک‌اند
    tags() {
      const ents = this.news?.entities || [];
      const out = [];
      const seen = new Set();
      for (const e of ents) {
        let t = null;
        if (e.team && e.team.slug) {
          t = {
            kind: 'TEAM', to: `/team/${e.team.slug}`,
            name: teamNames[e.team.name] || e.team.name,
            faName: teamNames[e.team.name] || null,
            rawName: e.team.name, shortName: null,
            logo: e.team.logo, icon: 'fa-shield-alt',
          };
        } else if (e.player && e.player.slug) {
          t = {
            kind: 'PLAYER', to: `/player/${e.player.slug}`,
            name: playerNames[e.player.name] || e.player.name,
            faName: playerNames[e.player.name] || null,
            rawName: e.player.name, shortName: null,
            logo: null, icon: 'fa-user',
          };
        } else if (e.competition && e.competition.id) {
          t = {
            kind: 'COMPETITION', to: `/competition/${e.competition.id}`,
            name: leagueNamesLatin[e.competition.name] || e.competition.name,
            faName: leagueNamesLatin[e.competition.name] || null,
            rawName: e.competition.name, shortName: null,
            logo: e.competition.logo, icon: 'fa-trophy',
          };
        } else if (e.coach && (e.coach.slug || e.coach.id)) {
          const fa = coachNameFa(e.coach.name);
          t = {
            kind: 'COACH', to: `/coach/${e.coach.slug || e.coach.id}`,
            name: fa,
            faName: fa !== e.coach.name ? fa : null,
            rawName: e.coach.name, shortName: null,
            logo: null, icon: 'fa-clipboard-list',
          };
        }
        if (t && !seen.has(t.to)) { seen.add(t.to); out.push(t); }
      }
      return out;
    },

    paragraphs() {
      if (!this.news?.content) return []

      return this.news.content
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<\/p>/gi, '\n')
          .replace(/<p[^>]*>/gi, '')
          .replace(/<[^>]+>/g, '')
          .split(/\n+/)
          .map(p => p.trim())
          .filter(p => p.length > 10)
    },

    // پاراگراف‌ها با لینک اَنتتی‌ها داخل متن — هر تگ فقط بار اول لینک می‌شود
    richParagraphs() {
      const paras = this.paragraphs;
      if (!paras.length) return [];
      if (!this.tags.length) return paras.map(text => ({ tokens: [{ text }] }));

      // برای هر تگ: نام فارسی اول، بعد نام دیتابیس (اولویت لینک با نام فارسی)
      const patterns = [];
      for (const tag of this.tags) {
        const names = [...new Set([tag.faName, tag.rawName, tag.shortName].filter(Boolean))];
        for (const name of names) {
          const re = nameToRegex(name);
          if (re) patterns.push({ tag, re });
        }
      }

      const used = new Set(); // هر تگ فقط یک‌بار در کل خبر لینک می‌شود
      return paras.map(text => {
        const hits = [];
        for (const p of patterns) {
          if (used.has(p.tag.to)) continue;
          const m = p.re.exec(text);
          if (m) hits.push({ start: m.index, end: m.index + m[0].length, tag: p.tag });
        }
        if (!hits.length) return { tokens: [{ text }] };

        hits.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));
        const tokens = [];
        let pos = 0;
        let links = 0;
        for (const h of hits) {
          if (h.start < pos) continue;          // هم‌پوشان با لینک قبلی
          if (links >= 5) break;                // سقف لینک هر پاراگراف
          if (h.start > pos) tokens.push({ text: text.slice(pos, h.start) });
          tokens.push({ text: text.slice(h.start, h.end), to: h.tag.to });
          used.add(h.tag.to);
          pos = h.end;
          links++;
        }
        if (pos < text.length) tokens.push({ text: text.slice(pos) });
        return { tokens };
      });
    },
  },

  methods: {
    async loadNews() {
      this.loading = true
      this.error = null
      try {
        const param = this.$route.params.slug
        // فرمت: "125-رونالدو-قراردادش-را-تمدید-کرد" یا فقط "125" یا slug فارسی —
        // اگر با هش ۸رقمی هگز تمام می‌شود قطعاً slug رسمی است (حتی اگر با رقم شروع شود)
        const looksSlugHash = /[0-9a-f]{8}$/.test(param)
        const idMatch = looksSlugHash ? null : param.match(/^(\d+)(?:$|-)/)
        const identifier = idMatch ? idMatch[1] : param
        this.news = await apiService.requestWithNotFoundRetry('/news/' + identifier)
        if (this.news) {
          // نسل قدیم URL (id-دار یا متفاوت از slug رسمی) → آدرس مرورگر به نسخهٔ
          // کانونیکال منتقل می‌شود؛ watcher این کامپوننت دوباره loadNews می‌زند
          // (سمت سرور هم ۳۰۱ واقعی داریم؛ این جایگزین آن برای محیط بدون هندلر است)
          let cur = param
          try { cur = decodeURIComponent(param) } catch (e) { /* خام */ }
          if (this.news.slug && this.news.slug !== cur) {
            this.$router.replace('/news/' + this.news.slug + '/')
            return
          }
          setSeo({
            title: `${this.news.title} | ضربه آزاد`,
            description: String(this.news.summary || this.news.title || '').slice(0, 160),
            image: this.news.imageUrl,
            // canonical به slug مرجع خبر — URLهای قدیمی id-دار به نسخهٔ slug تثبیت می‌شوند
            path: `/news/${this.news.slug || this.$route.params.slug}`,
          })
        } else {
          // خبر حذف‌شده/ناموجود (404 واقعی بک‌اند) → noindex تا Soft-404 زنجیره‌ای نشود؛
          // خطای سرور/شبکه (5xx/0) هرگز noindex نمی‌گیرد تا URL سالم حذف نشود
          setSeo({
            notFound: apiService.lastStatus === 404,
            title: 'خبر پیدا نشد | ضربه آزاد',
            path: `/news/${param}`,
          })
          this.error = 'خبر پیدا نشد'
        }
      } catch (e) {
        // فقط 404 واقعی noindex می‌گیرد؛ خطای سرور/شبکه URL سالم را noindex نمی‌کند
        setSeo({
          notFound: apiService.lastStatus === 404,
          title: 'خطا در نمایش خبر | ضربه آزاد',
          path: `/news/${this.$route.params.slug}`,
        })
        this.error = 'خطا در دریافت خبر'
      } finally {
        this.loading = false
      }
    },

    sourceLabel(source) {
      // اگر source یک آبجکت است (ساختار جدید API)

      if (source && typeof source === 'object') {
        const map = {varzesh3: 'ورزش۳', khabarvarzeshi: 'خبر ورزشی', isna: 'ایسنا', tasnim: 'تسنیم', yjc: 'باشگاه خبرنگاران جوان', tabnak: 'تابناک'}
        return map[source.name] || source.name || 'خبرگزاری'
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('fa-IR', {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    },

    proxyImage(url) {
      return url || null
    },

    onImgError(e) {
      e.target.parentElement.style.display = 'none'
    },
  },

  mounted() {
    this.loadNews()
  },

  watch: {
    '$route.params.slug'() {
      this.loadNews()
    }
  }
}
</script>

<style scoped>
.news-detail-page {
  min-height: 100vh;
  background: #f0f2f5;
  direction: rtl;
  font-family: 'Vazirmatn', sans-serif;
}

/* breadcrumb */
.breadcrumb {
  max-width: 860px;
  margin: 20px auto 0;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.82rem;
  color: #64748b;
}

.breadcrumb a {
  color: #3b82f6;
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

.breadcrumb i {
  font-size: 0.65rem;
}

.breadcrumb span {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 300px;
}

/* container */
.detail-container {
  max-width: 860px;
  margin: 0 auto;
  padding: 16px 20px 60px;
}

/* article */
.article {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  margin-top: 16px;
}

/* هدر */
.article-header {
  padding: 28px 32px 20px;
  border-bottom: 1px solid #f1f5f9;
}

.source-badge {
  display: inline-block;
  padding: 3px 12px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 14px;
}

/* رنگ badge-{source} از style.scss سراسری می‌آید */

.article-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.6;
  margin: 0 0 16px 0;
}

.article-meta {
  display: flex;
  gap: 20px;
  font-size: 0.82rem;
  color: #94a3b8;
  flex-wrap: wrap;
}

.article-meta span {
  display: flex;
  align-items: center;
  gap: 5px;
}

/* تصویر */
.article-image {
  width: 100%;
  max-height: 420px;
  overflow: hidden;
}

.article-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* خلاصه */
.article-summary {
  margin: 0;
  padding: 20px 32px;
  font-size: 1.05rem;
  font-weight: 600;
  color: #334155;
  line-height: 1.8;
  background: #f8fafc;
  border-right: 4px solid #3b82f6;
  border-bottom: 1px solid #f1f5f9;
}

/* محتوا */
.article-content {
  padding: 24px 32px;
  min-height: 120px;
}

.para {
  font-size: 0.97rem;
  color: #1e293b;
  line-height: 2;
  margin: 0 0 16px 0;
  text-align: justify;
}

.no-content {
  color: #94a3b8;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 20px 0;
}

/* تگ‌های مرتبط */
.article-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px 32px;
  border-bottom: 1px solid #f1f5f9;
}

.tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: none;
  background: #f1f5f9;
  color: #334155;
  border: 1px solid #e2e8f0;
  transition: all 0.15s;
}

.tag-chip:hover {
  background: #1e3a8a;
  color: #fff;
  border-color: #1e3a8a;
}

.tag-chip .tag-logo {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.tag-chip i {
  font-size: 0.72rem;
  color: #64748b;
}

.tag-chip:hover i {
  color: rgba(255, 255, 255, 0.8);
}

.tag-team i { color: #2563eb; }
.tag-player i { color: #16a34a; }
.tag-competition i { color: #d97706; }
.tag-coach i { color: #7c3aed; }

/* لینک اَنتتی داخل متن خبر */
.entity-link {
  color: #1d4ed8;
  font-weight: 600;
  text-decoration: none;
  border-bottom: 1px dashed #93c5fd;
  transition: all 0.15s;
}

.entity-link:hover {
  color: #1e3a8a;
  background: #dbeafe;
  border-bottom-color: #1e3a8a;
  border-radius: 3px;
}

/* فوتر */
.article-footer {
  padding: 20px 32px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.source-link {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 20px;
  background: #1e3a8a;
  color: #fff;
  border-radius: 8px;
  text-decoration: none;
  font-size: 0.88rem;
  transition: background 0.2s;
}

.source-link:hover {
  background: #1e40af;
}

.back-link {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #64748b;
  text-decoration: none;
  font-size: 0.88rem;
}

.back-link:hover {
  color: #1e3a8a;
}

/* حالت‌های خاص */
.loading-state, .error-state {
  text-align: center;
  padding: 100px 20px;
  color: #94a3b8;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #1e3a8a;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-state i {
  font-size: 3rem;
  display: block;
  margin-bottom: 12px;
}

.back-btn, .back-btn {
  display: inline-block;
  margin-top: 16px;
  padding: 9px 22px;
  background: #1e3a8a;
  color: #fff;
  border-radius: 8px;
  text-decoration: none;
}

@media (max-width: 600px) {
  .article-header, .article-summary, .article-content, .article-footer {
    padding-right: 18px;
    padding-left: 18px;
  }

  .article-title {
    font-size: 1.2rem;
  }

  .breadcrumb span {
    max-width: 150px;
  }
}
</style>